import IconChevronDown from "@shared/components/icons/ChevronDown"
import IconEllipses from "@shared/components/icons/Ellipses"
import IconRemoveCircle from "@shared/components/icons/RemoveCircle"
import { cx } from "@shared/components/untitledui/util/cx"
import { useContext } from "react"
import { Button, OverlayTriggerStateContext } from "react-aria-components"

type ClosedIcon = "ellipsis" | "chevron-down"

interface Props {
  label: string
  dataTest: string
  className?: string
  closedIcon?: ClosedIcon
}

// aria-hidden goes on the wrappers, not in the icons. RemoveCircle has other callers. The
// button's aria-label is the accessible name.
function TriggerIcon({ isOpen, closedIcon }: { isOpen: boolean; closedIcon: ClosedIcon }) {
  if (isOpen) {
    return (
      <span aria-hidden="true" className="block size-6" data-test="course header menu close icon">
        <IconRemoveCircle className="size-full fill-current" />
      </span>
    )
  } else {
    return (
      <span
        aria-hidden="true"
        className="block size-6"
        data-test={`course header menu ${closedIcon} icon`}
      >
        {closedIcon === "chevron-down" ? (
          <IconChevronDown className="size-full" />
        ) : (
          <IconEllipses className="size-full fill-current" />
        )}
      </span>
    )
  }
}

export default function CourseHeaderMenuTrigger({
  label,
  dataTest,
  className,
  closedIcon = "ellipsis",
}: Props) {
  const isOpen = useContext(OverlayTriggerStateContext)?.isOpen ?? false

  return (
    <Button
      aria-label={label}
      data-test={dataTest}
      className={cx(
        "size-6 shrink-0 cursor-pointer rounded-full text-black/40 outline-hidden hover:text-black/60 focus-visible:outline-2 focus-visible:-outline-offset-1 focus-visible:outline-focus-ring",
        // Closed, the trigger is a white chip like the AI button. Open, it has no chip.
        // black/40 on white is about 2.9:1. WCAG 1.4.11 requires 3:1 for a UI control, so the
        // dots are darker while the chip is there.
        !isOpen && "bg-white text-black/50 shadow-sm hover:text-black/70",
        className,
      )}
    >
      <TriggerIcon isOpen={isOpen} closedIcon={closedIcon} />
    </Button>
  )
}
